<script setup>
import { ref, reactive } from 'vue';

import SubscribeModal from '@/Shared/Modals/SubscribeModal';
import WhitelistModal from '@/Shared/Modals/WhitelistModal.vue';
import RainbooIcon from '@/Shared/SVG/RainbooIcon';
import SmsIcon from '@/Shared/SVG/SmsIcon';
import LinkedinIcon from '@/Shared/SVG/LinkedinIcon';
import TwitterIcon from '@/Shared/SVG/TwitterIcon';
import Locations from '@/Shared/Locations';
import ContactPanner from '@/Shared/ContactPanner';
import { Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/vue';
import BlogCard from '@/Shared/BlogCard.vue';
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/vue/24/solid';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';

let props = defineProps({
    data: Object,
    relatedBlogs: Object,
    team: Object,
    partners: Object,
    locations: Object,
    errors: Object,
});

let relatedBlogs = props.relatedBlogs.data;

let newsletterSettings = reactive({
    subscribeModalIsOpen: false,
});
let whitelistSettings = reactive({
    whitelistModalIsOpen: false,
    whitelistEmail: null,
});
</script>

<template>
    <SubscribeModal
        :open="newsletterSettings.subscribeModalIsOpen"
        :errors="errors"
        @close="newsletterSettings.subscribeModalIsOpen = false"
    >
    </SubscribeModal>

    <WhitelistModal
        :open="whitelistSettings.whitelistModalIsOpen"
        :email="whitelistSettings.whitelistEmail"
        :errors="errors"
        @close="whitelistSettings.whitelistModalIsOpen = false"
    >
    </WhitelistModal>

    <div class="flex h-full flex-col lg:flex-row lg:space-x-6">
        <div class="relative w-full">
            <div class="my-20 ml-0 flex h-[349px] lg:my-0 lg:ml-0 lg:h-screen">
                <div class="w-full m-auto">
                    <h2
                        class="-mt-4 w-full text-center text-3xl font-bold uppercase text-white lg:-mt-8 lg:w-3/4 lg:text-left lg:text-5xl"
                    >
                        {{ data.section_1.title }}
                    </h2>
                    <h2
                        class="mt-2 w-full text-center text-xl text-white lg:w-3/4 lg:text-left lg:text-2xl"
                    >
                        {{ data.section_1.title_2 }}
                    </h2>
                    <p
                        class="mt-6 w-full text-center font-light text-white lg:w-3/4 lg:text-left lg:text-xl"
                        v-html="data.section_1.content"
                    ></p>
                    <div class="mt-6 w-full lg:w-1/2">
                        <form
                            class="m-auto flex max-w-md lg:m-1"
                            @submit.prevent="
                                whitelistSettings.whitelistModalIsOpen = true
                            "
                        >
                            <label for="email-address" class="sr-only"
                                >Email address</label
                            >
                            <input
                                id="email-address"
                                name="email"
                                v-model="whitelistSettings.whitelistEmail"
                                type="email"
                                autocomplete="email"
                                required
                                class="min-w-0 flex-auto rounded-l-md border-0 bg-white bg-opacity-40 px-3.5 py-2 text-white placeholder-gray-200 sm:text-sm sm:leading-6"
                                placeholder="Enter your email"
                            />
                            <button
                                type="submit"
                                class="flex-none rounded-r-md bg-wgn-purple-2 px-6 py-2.5 text-lg font-semibold text-gray-300"
                            >
                                Join
                            </button>
                        </form>
                        <a
                            href="https://docs.wodo.io/wodo-gaming/"
                            target="_blank"
                            class="m-auto mt-4 inline-block flex-none rounded-md bg-wgn-purple-2 px-4 py-2.5 text-lg font-light text-gray-300 text-center w-full lg:w-auto"
                            >Whitepaper</a
                        >
                    </div>
                </div>
            </div>
            <!--            -->
            <div>
                <div class="container mt-3">
                    <div class="relative">
                        <a
                                :href="data.section_gamehub.link_url"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="game-hub-cta-wrapper"
                            >
                            <img
                                class="w-full"
                                :src="data.section_gamehub.image"
                                :alt="data.section_gamehub.title"
                            />
                            <div
                                class="absolute bottom-2 left-1/2 -translate-x-1/2 sm:bottom-7 lg:bottom-11"
                            >

                                    <div
                                        class="game-hub-cta flex min-w-[120px] items-center justify-center rounded-[40px] border py-2 text-sm font-bold sm:min-w-[165px] lg:py-3 lg:text-lg"
                                    >
                                        PLAY NOW
                                    </div>
                            </div>
                        </a>
                    </div>
                </div>
            </div>

            <!--            -->
            <div class="mt-9 w-full border-b border-wgn-purple-4">
                <RainbooIcon class="m-auto" />
            </div>
            <div class="mt-3">
                <h2 class="mb-7 text-center text-[22px] text-white">
                    {{ data.section_2.name }}
                </h2>
            </div>
            <!--            -->
            <div class="mt-8 hidden lg:block">
                <div
                    class="flex w-full flex-col justify-between space-y-4 rounded-2xl bg-wgn-purple-2 bg-cover bg-right p-10"
                    :style="
                        'background-image: url(' + data.section_2.image + ');'
                    "
                >
                    <div class="flex flex-col">
                        <div>
                            <svg
                                width="40"
                                height="40"
                                viewBox="0 0 40 40"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M28.9667 17.3V22.6833C28.9667 27.1666 27.1667 28.9667 22.6834 28.9667H17.3001C12.8167 28.9667 11.0167 27.1666 11.0167 22.6833V17.3C11.0167 12.8167 12.8167 11.0166 17.3001 11.0166H22.6834C27.1834 11.0333 28.9667 12.8167 28.9667 17.3Z"
                                    stroke="white"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                                <path
                                    d="M36.6667 9.61666V15C36.6667 19.4833 34.8667 21.2833 30.3833 21.2833H28.9667V17.3167C28.9667 12.8334 27.1667 11.0333 22.6833 11.0333H18.7167V9.61666C18.7167 5.13333 20.5167 3.33334 25 3.33334H30.3833C34.8667 3.33334 36.6667 5.13333 36.6667 9.61666Z"
                                    stroke="white"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                                <path
                                    d="M3.33337 30.3833V25C3.33337 20.5167 5.13337 18.7167 9.61671 18.7167H11.0334V22.6833C11.0334 27.1666 12.8334 28.9667 17.3167 28.9667H21.2834V30.3833C21.2834 34.8667 19.4834 36.6667 15 36.6667H9.61671C5.13337 36.6667 3.33337 34.8667 3.33337 30.3833Z"
                                    stroke="white"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </div>
                        <h1
                            class="mt-7 max-w-[395px] text-left text-3xl font-bold text-white"
                        >
                            {{ data.section_2.title }}
                        </h1>
                        <span
                            class="mt-4 max-w-[395px] text-left text-white text-opacity-60"
                            >{{ data.section_2.content }}</span
                        >
                    </div>
                    <Link
                        :href="route(data.section_2.route)"
                        class="w-fit rounded-[40px] bg-white px-6 py-2 text-base text-wgn-purple-2"
                        >{{ data.section_2.btn }}</Link
                    >
                </div>
            </div>
            <div class="mt-6 block lg:hidden">
                <div class="rounded-2xl bg-wgn-black-4 p-4">
                    <div class="flex flex-col">
                        <div>
                            <svg
                                width="40"
                                height="40"
                                viewBox="0 0 40 40"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M28.9667 17.3V22.6833C28.9667 27.1666 27.1667 28.9667 22.6834 28.9667H17.3001C12.8167 28.9667 11.0167 27.1666 11.0167 22.6833V17.3C11.0167 12.8167 12.8167 11.0166 17.3001 11.0166H22.6834C27.1834 11.0333 28.9667 12.8167 28.9667 17.3Z"
                                    stroke="white"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                                <path
                                    d="M36.6667 9.61666V15C36.6667 19.4833 34.8667 21.2833 30.3833 21.2833H28.9667V17.3167C28.9667 12.8334 27.1667 11.0333 22.6833 11.0333H18.7167V9.61666C18.7167 5.13333 20.5167 3.33334 25 3.33334H30.3833C34.8667 3.33334 36.6667 5.13333 36.6667 9.61666Z"
                                    stroke="white"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                                <path
                                    d="M3.33337 30.3833V25C3.33337 20.5167 5.13337 18.7167 9.61671 18.7167H11.0334V22.6833C11.0334 27.1666 12.8334 28.9667 17.3167 28.9667H21.2834V30.3833C21.2834 34.8667 19.4834 36.6667 15 36.6667H9.61671C5.13337 36.6667 3.33337 34.8667 3.33337 30.3833Z"
                                    stroke="white"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </div>
                        <span
                            class="mt-4 text-left text-2xl font-bold text-white"
                            >{{ data.section_2.title }}</span
                        >
                        <span
                            class="mt-2 text-left text-white text-opacity-60"
                            >{{ data.section_2.content }}</span
                        >
                        <Link
                            :href="route(data.section_2.route)"
                            class="mt-6 w-fit rounded-[40px] bg-white px-6 py-2 text-base text-wgn-purple-2"
                            >{{ data.section_2.btn }}</Link
                        >
                        <img
                            :src="data.section_2.mobile_image"
                            :alt="data.section_2.title"
                        />
                    </div>
                </div>
            </div>
            <!--            -->
            <div
                class="mt-5 flex flex-col space-x-0 space-y-5 lg:flex-row lg:space-x-5 lg:space-y-0"
            >
                <div
                    class="flex w-full flex-col justify-between space-y-4 rounded-2xl bg-wgn-purple-2 bg-cover bg-right p-10"
                    :style="
                        'background-image: url(' +
                        data.section_3.section_3_1.image +
                        ');'
                    "
                >
                    <div class="flex flex-col">
                        <div>
                            <svg
                                width="40"
                                height="40"
                                viewBox="0 0 40 40"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M36.6666 8.58331V14.75C36.6666 18.5 35.1666 20 31.4166 20H26.9166C23.1666 20 21.6666 18.5 21.6666 14.75V8.58331C21.6666 4.83331 23.1666 3.33331 26.9166 3.33331H31.4166C35.1666 3.33331 36.6666 4.83331 36.6666 8.58331Z"
                                    stroke="white"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                                <path
                                    d="M18.3334 25.25V31.4167C18.3334 35.1667 16.8334 36.6667 13.0834 36.6667H8.58337C4.83337 36.6667 3.33337 35.1667 3.33337 31.4167V25.25C3.33337 21.5 4.83337 20 8.58337 20H13.0834C16.8334 20 18.3334 21.5 18.3334 25.25Z"
                                    stroke="white"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                                <path
                                    d="M36.6667 25C36.6667 31.45 31.45 36.6667 25 36.6667L26.75 33.75"
                                    stroke="white"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                                <path
                                    d="M3.33337 15C3.33337 8.54998 8.55004 3.33331 15 3.33331L13.25 6.24998"
                                    stroke="white"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </div>
                        <span
                            class="mt-7 max-w-[395px] text-left text-3xl font-bold text-white"
                            >{{ data.section_3.section_3_1.title }}</span
                        >
                        <span
                            class="mt-4 text-left text-white text-opacity-60"
                            >{{ data.section_3.section_3_1.content }}</span
                        >
                    </div>
                    <Link
                        :href="route(data.section_3.section_3_1.route)"
                        class="w-fit rounded-[40px] bg-white px-6 py-2 text-base text-wgn-purple-2"
                        >{{ data.section_3.section_3_1.btn }}</Link
                    >
                </div>
                <div
                    class="flex w-full flex-col justify-between space-y-4 rounded-2xl bg-wgn-purple-2 bg-cover bg-right p-10"
                    :style="
                        'background-image: url(' +
                        data.section_3.section_3_2.image +
                        ');'
                    "
                >
                    <div class="flex flex-col">
                        <div>
                            <svg
                                width="40"
                                height="40"
                                viewBox="0 0 40 40"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M11.6666 13.3333H6.66665C4.83331 13.3333 3.33331 11.8333 3.33331 9.99998V6.66665C3.33331 4.83331 4.83331 3.33331 6.66665 3.33331H11.6666C13.5 3.33331 15 4.83331 15 6.66665V9.99998C15 11.8333 13.5 13.3333 11.6666 13.3333Z"
                                    stroke="#41008E"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                                <path
                                    d="M34.6667 11.6667H28.6667C27.5667 11.6667 26.6667 10.7666 26.6667 9.66665V7.00002C26.6667 5.90002 27.5667 5 28.6667 5H34.6667C35.7667 5 36.6667 5.90002 36.6667 7.00002V9.66665C36.6667 10.7666 35.7667 11.6667 34.6667 11.6667Z"
                                    stroke="#41008E"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                                <path
                                    d="M34.6667 24.1667H28.6667C27.5667 24.1667 26.6667 23.2666 26.6667 22.1666V19.5C26.6667 18.4 27.5667 17.5 28.6667 17.5H34.6667C35.7667 17.5 36.6667 18.4 36.6667 19.5V22.1666C36.6667 23.2666 35.7667 24.1667 34.6667 24.1667Z"
                                    stroke="#41008E"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                                <path
                                    d="M15 8.33331H26.6667"
                                    stroke="#292D32"
                                    stroke-width="1.5"
                                    stroke-miterlimit="10"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                                <path
                                    d="M20.8333 8.33331V30C20.8333 31.8333 22.3333 33.3333 24.1666 33.3333H26.6666"
                                    fill="white"
                                />
                                <path
                                    d="M20.8333 8.33331V30C20.8333 31.8333 22.3333 33.3333 24.1666 33.3333H26.6666"
                                    stroke="#292D32"
                                    stroke-width="1.5"
                                    stroke-miterlimit="10"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                                <path
                                    d="M20.8333 20.8333H26.6666"
                                    stroke="#292D32"
                                    stroke-width="1.5"
                                    stroke-miterlimit="10"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                                <path
                                    d="M34.6667 36.6667H28.6667C27.5667 36.6667 26.6667 35.7666 26.6667 34.6666V32C26.6667 30.9 27.5667 30 28.6667 30H34.6667C35.7667 30 36.6667 30.9 36.6667 32V34.6666C36.6667 35.7666 35.7667 36.6667 34.6667 36.6667Z"
                                    stroke="#292D32"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </div>
                        <span
                            class="mt-7 max-w-[395px] text-left text-3xl font-bold text-wgn-purple-2"
                            >{{ data.section_3.section_3_2.title }}</span
                        >
                        <span
                            class="mt-4 text-left text-wgn-purple-2 text-opacity-60"
                            >{{ data.section_3.section_3_2.content }}</span
                        >
                    </div>
                    <Link
                        :href="route(data.section_3.section_3_2.route)"
                        class="w-fit rounded-[40px] bg-wgn-purple-2 px-6 py-2 text-base text-white"
                        >{{ data.section_3.section_3_2.btn }}</Link
                    >
                </div>
            </div>
            <!--            -->
            <div class="mt-9 w-full border-b border-wgn-purple-4">
                <RainbooIcon class="m-auto" />
            </div>
            <div class="mt-3">
                <h2 class="mb-7 text-center text-[22px] text-white">
                    {{ data.section_4.title }}
                </h2>
            </div>
            <!--            -->
            <div class="mt-6 md:mt-11">
                <div
                    class="flex flex-row justify-start space-x-5 overflow-x-scroll pb-2"
                >
                    <div
                        class="flex max-w-[286px] shrink-0 flex-col justify-between space-y-24 rounded-2xl bg-wgn-blue-1 p-6"
                        v-for="product in data.section_4.products"
                    >
                        <div class="relative flex-1">
                            <div class="mb-4 h-[210px]">
                                <img
                                    class="w-full rounded-lg object-cover shadow-lg"
                                    :src="product.image"
                                    :alt="product.title"
                                />
                            </div>
                            <h3 class="text-2xl font-medium text-white">
                                {{ product.title }}
                            </h3>
                            <p
                                class="mt-2 text-base text-white text-opacity-60"
                            >
                                {{ product.content }}
                            </p>
                        </div>
                        <div>
                            <Link
                                :href="route(product.route)"
                                class="w-fit rounded-[40px] bg-white px-6 py-2 text-base text-wgn-purple-2"
                                >{{ product.btn }}</Link
                            >
                        </div>
                    </div>
                </div>
            </div>
            <!--            -->
            <div v-if="Object.keys(relatedBlogs).length">
                <div class="mt-28 w-full border-b border-wgn-purple-4">
                    <RainbooIcon class="m-auto"/>
                </div>
                <h4 class="text-xl text-white mt-2 text-center">Similar Articles</h4>
                <div class="flex flex-col mt-6 sm:mt-16 relative">
                    <swiper class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 w-full"
                            :breakpoints="{
                            1: {slidesPerView:1},
                            640: {slidesPerView:2},
                            1024: {slidesPerView:3}
                        }"
                            :space-between="16"
                            :loop="true"
                            :pagination="{
                          el: '.blog_pagination',
                          clickable: true,
                        }"
                            paginationClickable: true
                            :modules="[Pagination]"
                    >
                        <swiper-slide v-for="relatedBlog in relatedBlogs">
                            <BlogCard :blog="relatedBlog" :responsiveChangeStyle="false"></BlogCard>
                        </swiper-slide>
                    </swiper>
                    <div v-if="0" class="blog_swiper_button_prev">
                        <div class="flex h-4 w-4 rounded-full text-sm text-white items-center bg-white">
                            <ArrowLeftIcon class="text-gray-700 text-center mr-0.5 w-full m-auto"></ArrowLeftIcon>
                        </div>
                    </div>
                    <div class="blog_pagination text-center" slot="pagination"></div>
                    <div v-if="0" class="absolute blog_swiper_button_next">
                        <div class="rounded-full bg-white rounded-full w-8 h-8">
                            <ArrowRightIcon class="text-black h-full w-4 m-auto"></ArrowRightIcon>
                        </div>
                    </div>
                </div>
            </div>
            <!--            -->
            <div class="mt-16 w-full border-b border-wgn-purple-4 md:mt-28">
                <RainbooIcon class="m-auto" />
            </div>
            <div class="mt-3">
                <h2 class="mb-7 text-center text-[22px] text-white">
                    {{ data.section_5.title }}
                </h2>
            </div>
            <!--            -->
            <div class="mt-6 md:mt-9">
                <div
                    class="flex flex-row justify-start space-x-5 overflow-x-scroll pb-2 lg:justify-center"
                >
                    <div
                        class="flex max-w-[380px] shrink-0 flex-col justify-between rounded-2xl bg-wgn-blue-1 p-6"
                        v-for="item in team.slice(0, 3)"
                    >
                        <div class="mb-4">
                            <div class="aspect-w-1 aspect-h-1">
                                <img
                                    class="rounded-lg object-cover shadow-lg"
                                    :src="item.image"
                                    :alt="item.name"
                                />
                            </div>
                            <div class="mt-4 flex items-center justify-between">
                                <div class="flex flex-col justify-start">
                                    <span class="text-2xl text-white">{{
                                        item.name
                                    }}</span>
                                    <span
                                        class="text-white text-opacity-60"
                                        v-if="item.title !== null"
                                        >{{ item.title }}</span
                                    >
                                </div>
                                <div class="flex items-center space-x-6">
                                    <a
                                        :href="item.twitter"
                                        target="_blank"
                                        v-if="item.twitter !== null"
                                    >
                                        <TwitterIcon
                                            class="h-4 w-4 text-white opacity-40"
                                        ></TwitterIcon>
                                    </a>
                                    <a
                                        :href="item.linkedin"
                                        target="_blank"
                                        v-if="item.linkedin !== null"
                                    >
                                        <LinkedinIcon
                                            class="h-5 w-5"
                                        ></LinkedinIcon>
                                    </a>
                                </div>
                            </div>
                            <div class="mt-4">
                                <p class="text-white text-opacity-60">
                                    {{ item.brief }}
                                </p>
                            </div>
                        </div>
                        <div
                            class="border-t border-white border-opacity-10 pt-4"
                        >
                            <Link
                                :href="route('contact')"
                                class="flex items-center justify-start space-x-3.5"
                            >
                                <SmsIcon class="h-5 w-5 text-white"></SmsIcon>
                                <span class="text-white"
                                    >Contact {{ item.name }}</span
                                >
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
            <!--            -->
            <div class="mt-16 w-full border-b border-wgn-purple-4 md:mt-28">
                <RainbooIcon class="m-auto" />
            </div>
            <div class="mt-3">
                <h2 class="mb-7 text-center text-[22px] text-white">
                    {{ data.section_6.title }}
                </h2>
            </div>
            <!--            -->
            <div class="mt-6 md:mt-9">
                <div class="grid grid-cols-2 gap-5 md:grid-cols-4">
                    <div
                        v-for="item in partners"
                        class="flex h-[160px] rounded-2xl bg-wgn-blue-1"
                    >
                        <img class="m-auto" :src="item.logo" :alt="item.name" />
                    </div>
                </div>
            </div>
            <!--            -->
            <div class="mt-16 w-full border-b border-wgn-purple-4 md:mt-28">
                <RainbooIcon class="m-auto" />
            </div>
            <div class="mt-3">
                <h2 class="mb-7 text-center text-[22px] text-white">
                    {{ data.section_7.title }}
                </h2>
            </div>
            <div class="mt-7">
                <Locations :locations="locations"></Locations>
            </div>
            <!--            -->
            <div class="mt-28 mb-32 md:mt-20 md:mb-40">
                <ContactPanner></ContactPanner>
            </div>
        </div>
    </div>
</template>

<style scoped>
.game-hub-cta {
    background: linear-gradient(90deg, #6100d4 0%, #511d8e 53%, #32006e 100%);
    border: 1.5px solid #b5a6f2;
    color: #f6f4fe;
    transition: all 0.3s;
}
.game-hub-cta-wrapper:hover .game-hub-cta {
    background: linear-gradient(90deg, #360175 0%, #330d5f 53%, #32006e 100%);
    border: 1.5px solid rgba(126, 74, 203, 1);
}
</style>
